import useCases from '@/hooks/useCases';
import { formatDate } from '@/utils/dates';
import { Box, IconButton, useTheme } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { observer } from 'mobx-react';
import { useState, useRef, useLayoutEffect } from 'react';
import CaseDataBarItem from './CaseDataBarItem';
import { CaseDataBarLocationItem } from './CaseDataBarLocationItem';
import { CaseFlagReason } from './CaseFlagReason';
import { CaseDataBarPopover } from './CaseDataBarPopover';

export const CaseDataBar = observer(() => {
  const cases = useCases();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isCompact, setIsCompact] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullContentRef = useRef<HTMLDivElement>(null);

  const handleTogglePopover = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  // Check if content overflows and switch to compact mode if needed
  useLayoutEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const checkOverflow = () => {
      if (containerRef.current && fullContentRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const fullContentWidth = fullContentRef.current.scrollWidth;
        // Add a buffer to prevent flickering at the threshold
        const hasOverflow = fullContentWidth > containerWidth - 40;
        
        setIsCompact((prev) => {
          if (hasOverflow !== prev) {
            return hasOverflow;
          }
          return prev;
        });
      }
    };

    // Debounce the resize check to prevent rapid toggling
    const debouncedCheckOverflow = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkOverflow, 150);
    };

    // Initial check with small delay
    timeoutId = setTimeout(checkOverflow, 50);

    const resizeObserver = new ResizeObserver(debouncedCheckOverflow);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [cases.caseId, cases.cpr, cases.patientId, cases.externalId, cases.location]);

  // Full view - show all items in a row
  const fullContent = (
    <>
      {cases.isFlagged() && <CaseFlagReason urgent={cases.urgent} />}
      {cases.caseId && (
        <CaseDataBarItem title="Dato" value={formatDate(cases.createdAt)} showText />
      )}
      {cases.cpr && (
        <>
          <CaseDataBarItem title="Navn" value={cases.patientName} />
          <CaseDataBarItem title="CPR" value={`#${cases.cpr}`} />
        </>
      )}
      {!cases.cpr && cases.patientId && (
        <CaseDataBarItem
          title="Patient ID"
          value={cases.patientId}
          operation="OpenPatient"
          linkTo={cases.patientId}
        />
      )}
      {cases.caseId && <CaseDataBarItem title="Corti ID" value={cases.caseId} showText />}
      {cases.externalId && <CaseDataBarItem title="Journal ID" value={cases.externalId} showText />}
      {cases.caseId && <CaseDataBarLocationItem />}
    </>
  );

  // Compact view - show patient info with popover button
  const compactContent = (
    <>
      {cases.cpr && (
        <>
          <CaseDataBarItem title="Navn" value={cases.patientName} />
          <CaseDataBarItem title="CPR" value={`#${cases.cpr}`} />
        </>
      )}
      {!cases.cpr && cases.patientId && (
        <CaseDataBarItem
          title="Patient ID"
          value={cases.patientId}
          operation="OpenPatient"
          linkTo={cases.patientId}
        />
      )}
      <IconButton
        onClick={handleTogglePopover}
        size="small"
        sx={{
          color: anchorEl ? theme.palette.primary.main : theme.palette.grey[500],
          transition: 'transform 0.3s, color 0.3s',
          transform: anchorEl ? 'rotate(90deg)' : 'rotate(0deg)',
        }}
      >
        <MoreHorizIcon />
      </IconButton>
    </>
  );

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Hidden full content for measurement */}
        <Box
          ref={fullContentRef}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 3,
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {fullContent}
        </Box>
        
        {/* Visible content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: isCompact ? 1 : 3,
          }}
        >
          {isCompact ? compactContent : fullContent}
        </Box>
      </Box>
      {isCompact && <CaseDataBarPopover anchorEl={anchorEl} />}
      {isCompact && anchorEl && (
        <Box
          onClick={handleClosePopover}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
          }}
        />
      )}
    </>
  );
});
